import { SvcGetProductAvailability } from "@springtree/eva-services-core";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Fragment, useMemo, useState } from "react";

import { useWishlist } from "~/contexts/wishlist";
import { useEvaService } from "~/hooks/use-eva-service";
import { useProductsHierarchicalData } from "~/hooks/use-lines-container";
import Constants from "~/utils/constants";

import CartProductTileLater from "./cart-product-tile-later";
import { UndoItem } from "./cart-products";
import styles from "./cart-products.module.scss";
import CartUndoItem from "./cart-undo-item";

export default function CartSavedForLater() {
  const t = useTranslations();
  const { addProdToWishlist, removeProdFromWishlist, savedForLaterList } = useWishlist();
  const [undoList, setUndoList] = useState<UndoItem[]>([]);
  const evaEndpointUrl = process.env.NEXT_PUBLIC_EVA_BACKEND_URL;

  const getProductAvailability = useEvaService(SvcGetProductAvailability);

  const combinedList = useMemo(() => {
    let savedForLaterListProd: any[] = [];
    if (savedForLaterList?.Products) {
      savedForLaterListProd = [...savedForLaterList?.Products];
    }
    undoList.forEach((element) => {
      const sameProduct = savedForLaterListProd.find((line) => line.ProductID == element.product);
      if (!sameProduct) {
        savedForLaterListProd.splice(element.index, 0, element);
      }
    });
    return savedForLaterListProd;
  }, [savedForLaterList, undoList]);

  const combinedListProductIDs = useMemo(() => combinedList.map((line) => line.product_id) ?? [], [combinedList]);

  const linesProductsDetails = useProductsHierarchicalData(combinedList);

  const { data: saveForLaterAvailability } = useQuery({
    queryKey: ["saveForLaterAvailability", ...combinedListProductIDs],
    staleTime: Constants.DEFAULT_STALE_TIME,
    queryFn: async () => {
      if (!combinedListProductIDs?.length) {
        return null;
      }
      const results = await getProductAvailability({
        Products: combinedListProductIDs.map((line) => ({ ID: line, QuantityRequested: 1 })),
        Options: {
          Pickup: {
            IncludePickupOrganizationUnits: true,
            IncludeOrganizationUnitsWithoutStock: true,
          },
          Delivery: {
            AvailabilityDate: true,
          },
        },
      });
      return results;
    },
    enabled: !!evaEndpointUrl,
  });

  if (!savedForLaterList || (savedForLaterList.count === 0 && undoList.length === 0)) {
    return null;
  }

  const removeFromSavedForLaterListHandler = async (id: string, productName: string, itemIndex: number) => {
    await removeProdFromWishlist(id, savedForLaterList.ID);
    setUndoList((prevUndoList) => [
      ...prevUndoList,
      {
        product: id,
        productName,
        destinationList: "0", // TODO: check if this destination list id is correct
        index: itemIndex,
        removedAction: "fromSfl" as const,
        quantity: 1,
      },
    ]);
  };

  const removeUndoItem = (id: string) => {
    setUndoList((prevUndoList) => prevUndoList.filter((el) => el.product !== id));
  };

  const movedToWishlistHandler = (
    prodId: string,
    productName: string,
    destinationListId: string,
    itemIndex: number
  ) => {
    setUndoList((prevUndoList) => [
      ...prevUndoList,
      {
        product: prodId,
        productName,
        destinationList: destinationListId,
        index: itemIndex,
        removedAction: "toWishlist",
        quantity: 1,
      },
    ]);
  };

  const fromWishlistBackToSfl = async (id: string, destination: string) => {
    await removeProdFromWishlist(id, destination);
    await addProdToWishlist(id, `${savedForLaterList.ID}`);
    removeUndoItem(id);
  };
  const fromRemovedBackToSfl = async (id: string) => {
    await addProdToWishlist(id, `${savedForLaterList.ID}`);
    removeUndoItem(id);
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.savedForLaterTitle}>
        {t("generic.cart.saved_for_later")} ({savedForLaterList.count})
      </h3>
      {combinedList.map((line, idx) => {
        const isProductInStockDelivery = line.IsDigitalGiftCard
          ? true
          : saveForLaterAvailability?.Products?.find((p) => p.ProductID === line.product_id)?.Delivery?.HasStock;
        const isProductInStockPickup = line.IsDigitalGiftCard
          ? true
          : saveForLaterAvailability?.Products?.find((p) => p.ProductID === line.product_id)?.Pickup?.HasStock;

        return (
          <Fragment key={idx + "SFL"}>
            {idx !== 0 && <div className={styles.divider} aria-hidden />}
            {!line.removedAction ? (
              <CartProductTileLater
                line={line}
                onRemove={removeFromSavedForLaterListHandler}
                movedToWishlist={movedToWishlistHandler}
                outOfStockDelivery={isProductInStockDelivery === false}
                outOfStockPickup={isProductInStockPickup === false}
                itemIndex={idx}
                rootProductDetails={linesProductsDetails?.[line.product_id] || line}
              />
            ) : (
              <CartUndoItem
                undoItem={line}
                undoAction={() => {
                  if (line.removedAction === "toWishlist") {
                    fromWishlistBackToSfl(line.product, line.destinationList);
                  } else {
                    fromRemovedBackToSfl(line.product);
                  }
                }}
                removeUndoItem={() => removeUndoItem(line.product)}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}
